import { apiClient, createAuthHeaders } from "@/lib/api-client";
import { useSlideStore } from "@/stores/slide-store";

const STORAGE_KEY = "optidev-slides-export-job";

interface PendingExportJob {
  jobId: string;
  format: "pptx" | "pdf";
  title: string;
  projectId: string;
  createdAt: number;
}

/**
 * Save a pending export job to localStorage so it survives page reloads.
 */
export function savePendingJob(job: PendingExportJob): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(job));
}

/**
 * Get the pending export job from localStorage, if any.
 * Auto-clears jobs older than 10 minutes (stale).
 */
export function getPendingJob(): PendingExportJob | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;

  try {
    const job: PendingExportJob = JSON.parse(raw);
    // Clear stale jobs (older than 10 minutes)
    if (Date.now() - job.createdAt > 10 * 60 * 1000) {
      clearPendingJob();
      return null;
    }
    return job;
  } catch {
    clearPendingJob();
    return null;
  }
}

/**
 * Clear the pending export job from localStorage.
 */
export function clearPendingJob(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Resume polling a pending export job.
 * Updates store progress, downloads file on completion, cleans up on finish.
 */
export async function resumePendingExport(
  getIdToken: () => Promise<string | null>,
): Promise<void> {
  const job = getPendingJob();
  if (!job) return;

  const token = await getIdToken();
  if (!token) {
    clearPendingJob();
    return;
  }

  const headers = createAuthHeaders(token);
  const store = useSlideStore.getState();

  // Check current status first
  try {
    const { data } = await apiClient.get(
      `/api/slides/export/${job.jobId}/status`,
      { headers },
    );

    // Already done — either completed or failed
    if (data.status === "completed") {
      store.setIsExporting(true);
      store.setExportProgress(100);

      // Download the file
      const blob = await apiClient.get(
        `/api/slides/export/${job.jobId}/download`,
        { headers, responseType: "blob" },
      );

      downloadBlob(
        blob.data,
        data.result?.fileName ||
          `${job.title}.${job.format}`,
      );

      clearPendingJob();
      store.setIsExporting(false);
      store.setExportProgress(0);
      return;
    }

    if (data.status === "failed") {
      console.error("[SlideExport] Pending job failed:", data.error);
      clearPendingJob();
      return;
    }

    // Still in progress — resume polling
    store.setIsExporting(true);
    if (typeof data.progress === "number") {
      store.setExportProgress(data.progress);
    }

    const result = await pollUntilComplete(job.jobId, headers);

    if (result.status === "completed") {
      const blob = await apiClient.get(
        `/api/slides/export/${job.jobId}/download`,
        { headers, responseType: "blob" },
      );

      downloadBlob(
        blob.data,
        result.result?.fileName ||
          `${job.title}.${job.format}`,
      );
    } else {
      console.error("[SlideExport] Pending job failed:", result.error);
    }
  } catch (err) {
    console.error("[SlideExport] Failed to resume pending export:", err);
  } finally {
    clearPendingJob();
    store.setIsExporting(false);
    store.setExportProgress(0);
  }
}

async function pollUntilComplete(
  jobId: string,
  headers: Record<string, string>,
  maxAttempts = 120,
  intervalMs = 1000,
): Promise<{
  status: string;
  error?: string;
  result?: { fileName: string; fileSize: number };
}> {
  const store = useSlideStore.getState();

  for (let i = 0; i < maxAttempts; i++) {
    const { data } = await apiClient.get(
      `/api/slides/export/${jobId}/status`,
      { headers },
    );

    if (typeof data.progress === "number") {
      store.setExportProgress(data.progress);
    }

    if (data.status === "completed" || data.status === "failed") {
      return data;
    }

    await new Promise((r) => setTimeout(r, intervalMs));
  }

  throw new Error("Export timed out");
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
